import type { ProductAttributeValueDto, ProductAttributeValueSlotDto, ProductDto, ProductVariantDto } from '@repo/api/web/models'
import type { Ref } from 'vue'

type AttrId = string
type AttrValue = string

/** Ключевой атрибут с displayName и отсортированными значениями */
export interface KeyAttribute {
  attrId: string
  displayName: string
  values: ProductAttributeValueDto[]
}

/**
 * Composable для навигации по вариантам продукта.
 *
 * Формирует список ключевых атрибутов с их значениями,
 * определяет текущий выбранный вариант, проверяет доступность
 * значений и выполняет навигацию при выборе.
 */
export function useVariantsNavigation(
  product: Ref<ProductDto>,
  activeId: Ref<number>,
) {
  const { routing } = useProducts()

  /** Все доступные варианты (selfVariant + variants) без дубликатов по id */
  const allVariants = computed<ProductVariantDto[]>(() => {
    const result: ProductVariantDto[] = []
    const variantsData = product.value?.variants
    if (!variantsData) return result

    const seenIds = new Set<number>()

    if (variantsData.selfVariant) {
      result.push(variantsData.selfVariant)
      seenIds.add(variantsData.selfVariant.id)
    }

    if (variantsData.variants) {
      for (const v of variantsData.variants) {
        if (!seenIds.has(v.id)) {
          result.push(v)
          seenIds.add(v.id)
        }
      }
    }

    return result
  })

  /** Текущий вариант на основе activeId */
  const currentVariant = computed<ProductVariantDto | null>(() => {
    return allVariants.value.find(v => v.id === activeId.value) ?? null
  })

  /** Выбранные значения для каждого ключевого атрибута из keyVector текущего варианта */
  const selectedValues = computed<Record<AttrId, AttrValue>>(() => {
    if (!currentVariant.value?.keyVector) return {}

    const result: Record<AttrId, AttrValue> = {}
    const currentVariantVector = currentVariant.value.keyVector
    for (const [attrId, valueObj] of Object.entries(currentVariantVector)) {
      result[attrId] = (valueObj as ProductAttributeValueDto).value!
    }
    return result
  })

  /** Ключевые атрибуты с displayName из groups и отсортированными значениями */
  const keyAttributes = computed<KeyAttribute[]>(() => {
    const variantsData = product.value?.variants
    if (!variantsData?.keyWithValues) return []

    const keyWithValues = variantsData.keyWithValues as unknown as Record<
      string,
      ProductAttributeValueSlotDto
    >

    return Object.entries(keyWithValues).map(([attrId, config]) => {
      const displayName = config.metadata.displayName

      const sortedValues = [...config.values].sort((a, b) => {
        if (a.displayOrder == null && b.displayOrder === null) return 0
        if (a.displayOrder == null) return 1
        if (b.displayOrder == null) return -1
        return a.displayOrder! - b.displayOrder!
      })

      return { attrId, displayName, values: sortedValues }
    })
  })

  /**
   * Проверяет, доступно ли значение для выбора.
   * Значение доступно, если существует хотя бы один вариант,
   * чей keyVector содержит это значение И все текущие выбранные
   * значения для остальных атрибутов.
   */
  function isValueAvailable(attrId: AttrId, value: AttrValue): boolean {
    const testCombination: Record<AttrId, AttrValue> = { ...selectedValues.value }
    testCombination[attrId] = value

    return allVariants.value.some((variant) => {
      const keyVector = variant.keyVector as Record<string, ProductAttributeValueDto>
      return Object.entries(testCombination).every(
        ([attrId, attrValue]) => keyVector[attrId]?.value === attrValue,
      )
    })
  }

  /**
   * Выбирает значение для атрибута и выполняет навигацию
   * на страницу найденного варианта.
   */
  async function selectValue(attrId: string, value: string) {
    if (!isValueAvailable(attrId, value)) return

    const combination: Record<string, string> = { ...selectedValues.value }
    combination[attrId] = value

    const targetVariant = allVariants.value.find((variant) => {
      const keyVector = variant.keyVector as Record<string, ProductAttributeValueDto>
      return Object.entries(combination).every(
        ([key, val]) => keyVector[key]?.value === val,
      )
    })

    if (targetVariant && targetVariant.id !== activeId.value) {
      await navigateTo({
        name: 'ProductsDetails',
        params: { id: String(targetVariant.id) },
      })
    }
  }

  return {
    currentVariant,
    selectedValues,
    keyAttributes,

    isValueAvailable,
    selectValue,
  }
}

/**
 * Ищет displayName атрибута по его ID в группах атрибутов продукта.
 * Если не найдено — возвращает строку с ID.
 */
function findAttributeDisplayName(
  groups: ProductDto['groups'],
  attrId: string,
): string {
  for (const group of groups) {
    for (const attr of group.attributes ?? []) {
      if (String(attr.metadata.id) === attrId) {
        return attr.metadata.displayName
      }
    }
  }
  return attrId
}
